import { z } from 'zod';
import { ACCOUNT_CONNECTION_STATUSES, ACCOUNT_LOOKUP_STATUSES, FACT_SOURCES, FACT_STATUSES, FALLBACK_MODES, GAP_STATUSES, LIMITS, TENANT_STATUSES, USER_ROLES, VISITOR_MESSAGE_STATUSES, } from './constants.js';
/**
 * Zod schemas shared between the API (request validation) and the clients
 * (form validation + response types). One definition, both sides.
 */
export const uuidSchema = z.uuid();
export const tenantSlugSchema = z
    .string()
    .min(2)
    .max(LIMITS.tenantSlug)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Lowercase letters, digits and hyphens only');
/* ------------------------------------------------------------------ auth -- */
export const loginRequestSchema = z.object({
    email: z.email().max(LIMITS.email),
    password: z.string().min(1).max(LIMITS.password.max),
});
export const meResponseSchema = z.object({
    user: z.object({
        id: uuidSchema,
        email: z.email(),
        role: z.enum(['owner', 'staff']),
        /** Stonelink staff, not the tenant's — gates the platform dashboard. */
        platformAdmin: z.boolean(),
    }),
    tenant: z.object({
        id: uuidSchema,
        slug: z.string(),
        name: z.string(),
    }),
});
/**
 * Self-serve signup.
 *
 * `website` is a honeypot: the field is hidden, so a person never fills it and
 * anything in it came from a form-filling bot. It is accepted by the schema
 * rather than rejected, so the route can decide what to do about it.
 */
export const signupRequestSchema = z.object({
    businessName: z.string().trim().min(1).max(LIMITS.tenantName),
    email: z.email().max(LIMITS.email),
    password: z.string().min(LIMITS.password.min).max(LIMITS.password.max),
    website: z.string().max(200).optional(),
});
/* ------------------------------------------------------------------ team -- */
/**
 * Multiple people per workspace. `owner` configures and exposes the tenant;
 * `staff` operate the assistant's content but cannot touch settings, the embed
 * snippet, the backend connection or the team itself.
 */
export const teamMemberSchema = z.object({
    id: uuidSchema,
    email: z.email(),
    role: z.enum(USER_ROLES),
    createdAt: z.string(),
});
export const inviteSchema = z.object({
    id: uuidSchema,
    email: z.email(),
    role: z.enum(USER_ROLES),
    expiresAt: z.string(),
    createdAt: z.string(),
});
export const teamResponseSchema = z.object({
    members: z.array(teamMemberSchema),
    invites: z.array(inviteSchema),
});
export const createInviteRequestSchema = z.object({
    email: z.email().max(LIMITS.email),
    role: z.enum(USER_ROLES).default('staff'),
});
/** The accept link is returned once, at creation, and never again. */
export const createInviteResponseSchema = z.object({
    invite: inviteSchema,
    acceptUrl: z.string(),
});
export const acceptInviteRequestSchema = z.object({
    token: z.string().min(16).max(200),
    password: z.string().min(LIMITS.password.min).max(LIMITS.password.max),
});
/* -------------------------------------------------------------- settings -- */
export const tenantSettingsSchema = z.object({
    botName: z.string().min(1).max(LIMITS.botName),
    brandColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a hex colour like #2563eb'),
    logoUrl: z.url().max(500).nullable(),
    greeting: z.string().min(1).max(LIMITS.greeting),
    personaNotes: z.string().max(LIMITS.personaNotes),
    fallbackMode: z.enum(FALLBACK_MODES),
    contactInfo: z.string().max(LIMITS.contactInfo).nullable(),
});
export const updateSettingsRequestSchema = tenantSettingsSchema.partial();
/** Public, secret-free subset the widget fetches to theme itself. */
export const widgetConfigSchema = z.object({
    tenantSlug: z.string(),
    botName: z.string(),
    brandColor: z.string(),
    logoUrl: z.string().nullable(),
    greeting: z.string(),
    /**
     * Whether this tenant has a live account connection. A bare boolean — it only
     * tells the widget whether fetching an identity token is worth the round trip.
     */
    accountEnabled: z.boolean(),
});
/* ----------------------------------------------------------------- facts -- */
export const factSchema = z.object({
    id: uuidSchema,
    content: z.string(),
    source: z.enum(FACT_SOURCES),
    status: z.enum(FACT_STATUSES),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createFactRequestSchema = z.object({
    content: z.string().trim().min(3).max(LIMITS.factContent),
    source: z.enum(FACT_SOURCES).default('manual'),
});
export const updateFactRequestSchema = z.object({
    content: z.string().trim().min(3).max(LIMITS.factContent).optional(),
    status: z.enum(FACT_STATUSES).optional(),
});
export const listFactsQuerySchema = z.object({
    q: z.string().max(200).optional(),
    status: z.enum(FACT_STATUSES).optional(),
});
/* ------------------------------------------------------------------ gaps -- */
export const gapSchema = z.object({
    id: uuidSchema,
    question: z.string(),
    status: z.enum(GAP_STATUSES),
    conversationId: uuidSchema.nullable(),
    resolvedFactId: uuidSchema.nullable(),
    createdAt: z.string(),
});
export const resolveGapRequestSchema = z.object({
    content: z.string().trim().min(3).max(LIMITS.factContent),
});
/* ------------------------------------------------------ visitor messages -- */
export const visitorMessageSchema = z.object({
    id: uuidSchema,
    contact: z.string(),
    body: z.string(),
    status: z.enum(VISITOR_MESSAGE_STATUSES),
    conversationId: uuidSchema.nullable(),
    createdAt: z.string(),
});
export const updateVisitorMessageRequestSchema = z.object({
    status: z.enum(VISITOR_MESSAGE_STATUSES),
});
/* ------------------------------------------------------ account connector -- */
/**
 * A tenant declares the account lookups their backend supports in a manifest,
 * and each one becomes a tool the model may call. Because those declarations
 * come from outside and end up in a system prompt, the accepted JSON Schema is
 * a deliberately narrow subset: flat objects of scalar parameters, nothing
 * nested, nothing recursive, no `$ref`.
 */
export const accountActionNameSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,39}$/, 'Lowercase letters, digits and underscores only, starting with a letter');
const accountParamPropertySchema = z.object({
    type: z.enum(['string', 'number', 'integer', 'boolean']),
    description: z.string().max(300).optional(),
    enum: z
        .array(z.union([z.string(), z.number()]))
        .min(1)
        .max(20)
        .optional(),
});
/** Max parameters on a single action. Keeps the tool schema legible to the model. */
export const MAX_ACCOUNT_ACTION_PARAMS = 8;
export const accountActionParametersSchema = z
    .object({
    type: z.literal('object'),
    properties: z
        .record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/), accountParamPropertySchema)
        .refine((properties) => Object.keys(properties).length <= MAX_ACCOUNT_ACTION_PARAMS, `At most ${MAX_ACCOUNT_ACTION_PARAMS} parameters per action`),
    required: z.array(z.string()).max(MAX_ACCOUNT_ACTION_PARAMS).optional(),
})
    .refine((schema) => (schema.required ?? []).every((key) => key in schema.properties), 'Every `required` entry must name a declared property');
export const accountActionSchema = z.object({
    name: accountActionNameSchema,
    description: z.string().trim().min(1).max(LIMITS.accountActionDescription),
    parameters: accountActionParametersSchema,
});
/**
 * Actions arrive as `unknown` so one malformed entry can be reported with a
 * reason instead of discarding the whole manifest — a silently dropped action
 * reads to the owner as "the bot is broken".
 */
export const accountManifestSchema = z.object({
    version: z.literal(1).optional(),
    actions: z.array(z.unknown()).max(100),
});
export const rejectedAccountActionSchema = z.object({
    name: z.string(),
    reason: z.string(),
});
/** What the console shows. Secrets are never returned, only their presence. */
export const accountConnectionSchema = z.object({
    status: z.enum(ACCOUNT_CONNECTION_STATUSES),
    baseUrl: z.string().nullable(),
    hasIdentitySecret: z.boolean(),
    hasOutboundSecret: z.boolean(),
    manifestFetchedAt: z.string().nullable(),
    manifestError: z.string().nullable(),
    actions: z.array(accountActionSchema),
    rejectedActions: z.array(rejectedAccountActionSchema),
});
export const updateAccountConnectionRequestSchema = z.object({
    status: z.enum(ACCOUNT_CONNECTION_STATUSES).optional(),
    baseUrl: z.url().max(LIMITS.accountBaseUrl).nullable().optional(),
    outboundSecret: z
        .string()
        .min(LIMITS.accountSecret.min)
        .max(LIMITS.accountSecret.max)
        .nullable()
        .optional(),
});
/** Response to rotating the identity secret. Shown once and never again. */
export const identitySecretResponseSchema = z.object({
    identitySecret: z.string(),
});
export const testAccountLookupRequestSchema = z.object({
    action: accountActionNameSchema,
    subject: z.string().trim().min(1).max(LIMITS.endUserRef),
    params: z.record(z.string(), z.unknown()).default({}),
});
export const testAccountLookupResponseSchema = z.object({
    status: z.enum(ACCOUNT_LOOKUP_STATUSES),
    durationMs: z.number().int(),
    httpStatus: z.number().int().nullable(),
    data: z.unknown().optional(),
    error: z.string().nullable(),
});
export const accountLookupSchema = z.object({
    id: uuidSchema,
    conversationId: uuidSchema.nullable(),
    endUserRef: z.string(),
    action: z.string(),
    status: z.enum(ACCOUNT_LOOKUP_STATUSES),
    httpStatus: z.number().int().nullable(),
    durationMs: z.number().int(),
    errorMessage: z.string().nullable(),
    createdAt: z.string(),
});
/* --------------------------------------------------------- conversations -- */
export const conversationSummarySchema = z.object({
    id: uuidSchema,
    channel: z.enum(['widget', 'training']),
    visitorId: z.string().nullable(),
    /** Set when the visitor was signed in to their account on the host site. */
    endUserLabel: z.string().nullable(),
    createdAt: z.string(),
    messageCount: z.number().int(),
    lastMessageAt: z.string().nullable(),
    preview: z.string().nullable(),
    visitorMessageCount: z.number().int(),
});
export const transcriptMessageSchema = z.object({
    id: uuidSchema,
    role: z.enum(['user', 'assistant']),
    content: z.string(),
    createdAt: z.string(),
});
export const conversationDetailSchema = z.object({
    conversation: conversationSummarySchema,
    messages: z.array(transcriptMessageSchema),
    visitorMessages: z.array(visitorMessageSchema),
    /** What the assistant looked up in the tenant's backend during this chat. */
    accountLookups: z.array(accountLookupSchema),
});
/* ---------------------------------------------------------------- widget -- */
/**
 * `authToken` is the tenant-signed identity token the host page minted for the
 * signed-in visitor. It is optional everywhere: absent means an anonymous
 * visitor, which is the default and behaves exactly as it always has.
 */
export const widgetSessionRequestSchema = z.object({
    visitorId: z.string().min(8).max(64).optional(),
    conversationId: uuidSchema.optional(),
    // Nullish rather than optional: a client that always sends the field and
    // fills it with null when anonymous must not get a 400 for it.
    authToken: z.string().max(LIMITS.accountToken).nullish(),
});
export const widgetSessionResponseSchema = z.object({
    conversationId: uuidSchema,
    visitorId: z.string(),
    messages: z.array(transcriptMessageSchema),
    /** Display label for the signed-in visitor, or null when anonymous. */
    endUserLabel: z.string().nullable(),
});
export const widgetChatRequestSchema = z.object({
    conversationId: uuidSchema,
    visitorId: z.string().min(8).max(64),
    message: z.string().trim().min(1).max(4000),
    // Nullish rather than optional: a client that always sends the field and
    // fills it with null when anonymous must not get a 400 for it.
    authToken: z.string().max(LIMITS.accountToken).nullish(),
});
/* -------------------------------------------------------------- training -- */
export const trainingChatRequestSchema = z.object({
    conversationId: uuidSchema.optional(),
    message: z.string().trim().min(1).max(4000),
});
/* -------------------------------------------------------- platform admin -- */
/**
 * The Stonelink-internal view across every tenant. Everything here crosses the
 * tenant boundary on purpose, which is why it lives behind its own guard, its
 * own repository and its own test file.
 */
export const platformTenantSummarySchema = z.object({
    id: uuidSchema,
    slug: z.string(),
    name: z.string(),
    status: z.enum(TENANT_STATUSES),
    createdAt: z.string(),
    memberCount: z.number().int(),
    factCount: z.number().int(),
    messagesToday: z.number().int(),
    inputTokens30d: z.number().int(),
    outputTokens30d: z.number().int(),
});
export const platformTenantDetailSchema = z.object({
    tenant: platformTenantSummarySchema,
    members: z.array(teamMemberSchema),
});
export const updateTenantStatusRequestSchema = z.object({
    status: z.enum(TENANT_STATUSES),
});
/* ----------------------------------------------------------------- admin -- */
export const createTenantRequestSchema = z.object({
    slug: tenantSlugSchema,
    name: z.string().trim().min(1).max(LIMITS.tenantName),
    ownerEmail: z.email().max(LIMITS.email),
    ownerPassword: z.string().min(LIMITS.password.min).max(LIMITS.password.max),
});
export const embedSnippetResponseSchema = z.object({
    snippet: z.string(),
    widgetUrl: z.string(),
    tenantSlug: z.string(),
});
//# sourceMappingURL=schemas.js.map